/**
 * `GET /v1/diagnose` — why an embed is not working, in one call.
 *
 * This exists because a browser cannot find out for itself. When the per-instance
 * CORS middleware rejects a request it answers 403 with NO
 * `Access-Control-Allow-Origin`, so `fetch` rejects before a `Response` object
 * ever exists: the widget sees an opaque `TypeError` and cannot read the status,
 * the body, or anything else. An unknown key, a disabled instance, an origin that
 * is not allowlisted, and a CSP block are all literally the same event from
 * inside the failing page.
 *
 * So this endpoint is deliberately NOT origin-gated (it is the one thing that
 * must answer the page that is broken) and carries `Access-Control-Allow-Origin: *`.
 * Two rules keep that safe:
 *
 *  1. It NEVER returns the allowlist. It only answers "does the origin I sent
 *     work?", echoing back what the caller already supplied. Returning the list
 *     would turn it into an enumeration oracle for a tenant's internal and
 *     staging hostnames.
 *  2. `allowlistEmpty` is a boolean, never a count — it names the pathological
 *     case (an instance that blocks everybody) without leaking contents.
 *
 * The public key is required to ask, and the public key already ships in every
 * embedding page's HTML, so no new secret is involved.
 */
import type { z } from 'zod';
import { boolean, enum as zEnum, number, object, string } from 'zod';

import { WidgetInstanceStatusSchema } from './enums.js';

/**
 * Why the embed does or does not work. Ordered by how early the check fails, so
 * the first failing condition is the one reported.
 */
export const DiagnoseReasonSchema = zEnum([
  /** Everything checks out: key resolves, instance active, origin allowed. */
  'ok',
  /** No such public key. Usually a typo, or a key from a different environment. */
  'unknown_key',
  /** The key resolves but an operator has switched the instance off. */
  'instance_disabled',
  /** The key is fine; this origin is not on the instance's allowlist. */
  'origin_not_allowed',
  /** Called without an `Origin` header (e.g. curl), so no origin verdict exists. */
  'no_origin_sent',
]);
export type DiagnoseReason = z.infer<typeof DiagnoseReasonSchema>;

/**
 * The instance a key points at. `name` is the field that matters: it is what
 * tells an integrator they are wired to a *test* instance rather than the one
 * they meant. It is a new disclosure to anyone holding the public key, accepted
 * deliberately — the key is already public, and branding is already served by
 * `/v1/config`.
 */
export const DiagnoseInstanceSchema = object({
  name: string(),
  status: WidgetInstanceStatusSchema,
  captureLevel: number().int().min(0).max(3),
});

/** The origin verdict. Only ever echoes what the caller sent. */
export const DiagnoseOriginSchema = object({
  /** The raw `Origin` header as received, or null when none was sent. */
  sent: string().nullable(),
  /** Its canonical form, or null when absent/unparseable. */
  normalized: string().nullable(),
  /** Whether this origin may call the ingest API for this key. */
  allowed: boolean(),
});

/** Individual checks, so a caller can branch without parsing prose. */
export const DiagnoseChecksSchema = object({
  keyResolves: boolean(),
  instanceActive: boolean(),
  originAllowed: boolean(),
  /** True when the instance allows NOBODY — a dark widget. Never a count. */
  allowlistEmpty: boolean(),
});

export const DiagnoseResponseSchema = object({
  ok: boolean(),
  reason: DiagnoseReasonSchema,
  /** Absent when the key does not resolve — there is no instance to describe. */
  instance: DiagnoseInstanceSchema.nullable(),
  origin: DiagnoseOriginSchema,
  checks: DiagnoseChecksSchema,
  /** One sentence an integrator can act on directly. */
  remediation: string(),
});
export type DiagnoseResponse = z.infer<typeof DiagnoseResponseSchema>;
